<script>
  /* global Global */
  import Header from '../common/Header.svelte';
  import Footer from '../common/Footer.svelte';
  import Loading from '../common/Loading.svelte';
  import Error from '../common/Error.svelte';
  import Title from '../common/Title.svelte';
  import SmallTitle from '../common/SmallTitle.svelte';
  import Button from '../common/Button.svelte';
  import ImportantLink from '../ui-components/ImportantLink.svelte';
  import YearsSelect from '../ui-components/YearsSelect.svelte';
  import TwSelectMenu from '../tailwind/TwSelectMenu.svelte';
  import Schedule from '../ui-components/Schedule.svelte';
  import AccompanyingEventsBox from '../ui-components/AccompanyingEventsBox.svelte';
  import PageBox from '../ui-components/PageBox.svelte';
  import Common from '../js/common.js';

  export let params;

  if (!Global.pages.includes('schedule-and-results')) {
    window.location.href = window.localStorage.getItem('currentPage') || './';
  }

  let lang;

  let user = JSON.parse(window.localStorage.getItem('user'));
  const selectedYear = Number(params?.year) || Global.years[0];

  let allGames = [];
  let games = [];
  let accompanyingEvents = [];
  let teams = [];
  let fields = [];
  let importantLinks = [];
  let page;

  const dayElements = [{ value: 'all', label: { cs: 'Všechny dny', en: 'All days' } }];
  const groupElements = [{ value: 'all', label: { cs: 'Všechny skupiny', en: 'All groups' } }];
  const teamElements = [{ value: 'all', label: { cs: 'Všechny týmy', en: 'All teams' } }];
  const fieldElements = [{ value: 'all', label: { cs: 'Všechna hřiště', en: 'All fields' } }];

  let loading = true;
  let error = false;

  (async () => {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/scheduleAndResults/get/${selectedYear}`);

    if (response.ok) {
      const data = await response.json();

      allGames = data.games;
      accompanyingEvents = data.accompanyingEvents;
      teams = data.teams;
      fields = data.fields;
      importantLinks = data.importantLinks;
      page = data.page;
      user = data.user;

      dayElements.push(...Common.unique(allGames.map(value => value.date))
        .map(value => ({ value, label: Common.getDayOfWeek(value), append: value })));
      groupElements.push(...Common.unique(allGames.filter(value => value.group && isNaN(value.group)).map(value => value.group)).sort()
        .map(value => ({ value: value, label: value.toUpperCase() })));

      teamElements.push(...Common.sortArrayOfObjectsByKey(
        teams.map(value => ({ value: value.code, label: value.name, append: value.shortcut.toUpperCase() })),
        ['append'],
      ));
      fieldElements.push(...Common.sortArrayOfObjectsByKey(
        fields.map(value => ({ value: value.code, label: value.city, append: value.shortcut.toUpperCase() })),
        ['append'],
      ));

      accompanyingEvents = Common.sortArrayOfObjectsByKey(accompanyingEvents, ['from.date'], true);

      if (user) window.localStorage.setItem('user', JSON.stringify(data.user));
      else window.localStorage.removeItem('user');
    } else {
      error = true;
    }

    loading = false;
  })();

  let scroll;

  let day;
  let group;
  let team;
  let field;

  async function updateSchedule () {
    // eslint-disable-next-line no-undef
    await fetch(`${Common.BACKEND_URL}/scheduleAndResults/update`);
  }

  const i18n = {
    pageTitle: {
      cs: `Rozpis a výsledky | ${Global.project.name.cs}`,
      en: `Schedule and results | ${Global.project.name.en}`,
    },
    title: {
      cs: 'Rozpis a výsledky',
      en: 'Schedule and results',
    },
    closestGames: {
      cs: 'Nejbližší zápasy',
      en: 'Closest games',
    },
    findByDay: {
      cs: 'Vyhledat podle dne',
      en: 'Find by day',
    },
    findByGroup: {
      cs: 'Vyhledat podle skupiny',
      en: 'Find by group',
    },
    findByTeam: {
      cs: 'Vyhledat podle týmu',
      en: 'Find by team',
    },
    findByField: {
      cs: 'Vyhledat podle hřiště',
      en: 'Find by field',
    },
    accompanyingEvents: {
      cs: 'Doprovodné akce',
      en: 'Accompanying events',
    },
    updateSchedule: {
      cs: 'Aktualizovat rozpis',
      en: 'Update schedule',
    },
    editAccompanyingEvents: {
      cs: 'Upravit doprovodné akce',
      en: 'Edit accompanying events',
    },
    editImportantLinks: {
      cs: 'Upravit důležité odkazy',
      en: 'Edit important links',
    },
  };

  $: games = allGames.filter(game => {
    if (day !== 'all' && game.date !== day) return false;
    if (group !== 'all' && game.group !== group) return false;
    if (team !== 'all' && game.home.code !== team && game.visitor.code !== team) return false;
    if (field !== 'all' && game.field.code !== field) return false;

    return true;
  });

  const projectStyle = Global.project.style;
  const years = Global.years;
</script>

<svelte:head>
  <title> {i18n.pageTitle[lang]} </title>
</svelte:head>

<Header page='schedule-and-results{selectedYear !== years[0] ? `/${selectedYear}` : ''}' bind:lang bind:user/>

<Title title={i18n.title[lang]} color={projectStyle === 'sharp' ? 'purple' : 'red'} img='title/schedule-and-results.jpg' bind:lang bind:user/>

<div class='schedule-and-results'>
  <Loading bind:display={loading}/>
  <Error bind:display={error} bind:lang/>

  {#if !loading && !error}
    {#if importantLinks.length > 0 || user}
      <div class='schedule-and-results-important-links'>
        {#if user}
          <div class='schedule-and-results-important-links-admin'>
            <Button value={i18n.editImportantLinks[lang]} icon='edit' admin={true} disabled={!user.roles.includes('ROLE_ADMIN')}
              onClick={() => { window.location.href = './#/admin/schedule-and-results-important-links'; }}/>
          </div>
        {/if}

        {#if importantLinks.length > 0}
          <div class='schedule-and-results-important-links-scroll'>
            <Button value={i18n.closestGames[lang]} icon='south' onClick={scroll}/>
          </div>
        {/if}

        {#each importantLinks as importantLink}
          <div class='schedule-and-results-important-links-important-link'>
            <ImportantLink value={importantLink} short={true} bind:lang/>
          </div>
        {/each}
      </div>
    {/if}

    <YearsSelect page='schedule-and-results/:year' selectedYear={selectedYear} years={years} bind:lang/>

    <div class='schedule-and-results-find-by'>
      <div class='schedule-and-results-find-by-day'>
        <TwSelectMenu bind:value={day} label={i18n.findByDay[lang]} elements={dayElements} bind:lang/>
      </div>

      <div class='schedule-and-results-find-by-group'>
        <TwSelectMenu bind:value={group} label={i18n.findByGroup[lang]} elements={groupElements} bind:lang/>
      </div>

      <div class='schedule-and-results-find-by-team'>
        <TwSelectMenu bind:value={team} label={i18n.findByTeam[lang]} elements={teamElements} bind:lang/>
      </div>

      <div class='schedule-and-results-find-by-field'>
        <TwSelectMenu bind:value={field} label={i18n.findByField[lang]} elements={fieldElements} bind:lang/>
      </div>
    </div>

    <div class='schedule-and-results-content'>
      <div class='schedule-and-results-content-main'>
        {#if user}
          <div class='schedule-and-results-content-main-admin'>
            <Button value={i18n.updateSchedule[lang]} icon='refresh' admin={true} disabled={!user.roles.includes('ROLE_ADMIN')}
              onClick={async () => { await updateSchedule(); }}/>
          </div>
        {/if}

        <Schedule bind:games={games} year={selectedYear} bind:scroll bind:lang bind:user/>
      </div>

      {#if accompanyingEvents.length > 0 || user}
        <div class='schedule-and-results-content-right-panel'>
          {#if user}
            <div class='schedule-and-results-content-right-panel-admin'>
              <Button value={i18n.editAccompanyingEvents[lang]} icon='edit' admin={true} disabled={!user.roles.includes('ROLE_ADMIN')}
                onClick={() => { window.location.href = './#/admin/accompanying-events'; }}/>
            </div>
          {/if}

          <SmallTitle value={i18n.accompanyingEvents[lang]}/>

          <AccompanyingEventsBox accompanyingEvents={accompanyingEvents} bind:lang/>
        </div>
      {/if}
    </div>

    <PageBox value={page} page='schedule-and-results' bind:lang bind:user/>
  {/if}
</div>

<Footer bind:lang/>

<style>
  .schedule-and-results {
    display: flex;
    flex-direction: column;
    gap: 40px;

    max-width: 1450px;

    padding: 40px 60px;

    margin: auto;
  }

  .schedule-and-results-important-links {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  .schedule-and-results-important-links-admin {
    width: 100%;
  }

  .schedule-and-results-important-links-scroll {
    flex-grow: 1;
    flex-basis: calc((100% - 80px) / 5);
  }

  .schedule-and-results-important-links-important-link {
    flex-grow: 1;
    flex-basis: calc((100% - 80px) / 5);
  }

  .schedule-and-results-find-by {
    display: flex;
    flex-wrap: wrap;
    column-gap: 40px;
    row-gap: 20px;
  }

  .schedule-and-results-find-by-day,
  .schedule-and-results-find-by-group,
  .schedule-and-results-find-by-team,
  .schedule-and-results-find-by-field {
    width: calc((100% - 120px) / 4);
  }

  .schedule-and-results-content {
    display: flex;
    justify-content: space-between;
    gap: 40px;
  }

  .schedule-and-results-content-main {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 40px;

    width: calc((100% - 40px) / 3 * 2);
  }

  .schedule-and-results-content-right-panel {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 40px;

    width: calc((100% - 40px) / 3);
  }

  @media only screen and (max-width: 1400px) {
    .schedule-and-results-content {
      flex-direction: column;
    }

    .schedule-and-results-content-main {
      width: 100%;
    }

    .schedule-and-results-content-right-panel {
      width: 100%;
    }
  }

  @media only screen and (max-width: 1300px) {
    .schedule-and-results-important-links-scroll {
      flex-basis: calc((100% - 40px) / 3);
    }

    .schedule-and-results-important-links-important-link {
      flex-basis: calc((100% - 40px) / 3);
    }
  }

  @media only screen and (max-width: 900px) {
    .schedule-and-results {
      padding: 40px;
    }

    .schedule-and-results-important-links-scroll {
      flex-basis: calc((100% - 20px) / 2);
    }

    .schedule-and-results-important-links-important-link {
      flex-basis: calc((100% - 20px) / 2);
    }
  }

  @media only screen and (max-width: 850px) {
    .schedule-and-results-find-by-day,
    .schedule-and-results-find-by-group,
    .schedule-and-results-find-by-team,
    .schedule-and-results-find-by-field {
      width: calc((100% - 40px) / 2);
    }
  }

  @media only screen and (max-width: 640px) {
    .schedule-and-results-find-by-day,
    .schedule-and-results-find-by-group,
    .schedule-and-results-find-by-team,
    .schedule-and-results-find-by-field {
      width: 100%;
    }
  }

  @media only screen and (max-width: 600px) {
    .schedule-and-results-important-links-scroll {
      flex-basis: 100%;
    }

    .schedule-and-results-important-links-important-link {
      flex-basis: 100%;
    }
  }

  @media only screen and (max-width: 350px) {
    .schedule-and-results {
      padding: 40px 20px;
    }
  }
</style>
